import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CurrencyEur, Funnel, X, CaretLeft, CaretRight, Printer } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, formatMonth } from './formatters';

export function PaymentsSection({ teachers, anno }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [totals, setTotals] = useState({ total_amount: 0, paid_amount: 0, pending_amount: 0 });
  const [filterTeacherId, setFilterTeacherId] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterStudentName, setFilterStudentName] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAnnoCompetenza, setFilterAnnoCompetenza] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableAnniCompetenza, setAvailableAnniCompetenza] = useState([]);
  const [dataEsportazione, setDataEsportazione] = useState('');

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (filterTeacherId) params.teacher_id = filterTeacherId;
      if (anno) params.anno = anno;
      if (filterMonth && filterMonth !== 'all') params.month = filterMonth;
      if (filterStudentName) params.student_name = filterStudentName;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      if (filterAnnoCompetenza && filterAnnoCompetenza !== 'all') params.anno_competenza = filterAnnoCompetenza;
      if (sortBy) params.sort_by = sortBy;
      const response = await api.getAdminPayments(params);
      const data = response.data;
      setPayments(data.records);
      setTotal(data.total);
      setPages(data.pages);
      setTotals(data.totals);
      if (data.available_months) setAvailableMonths(data.available_months);
      if (data.available_anni_competenza) setAvailableAnniCompetenza(data.available_anni_competenza);
      if (data.data_esportazione) setDataEsportazione(data.data_esportazione);
    } catch { toast.error('Errore nel caricamento dei pagamenti'); }
    finally { setLoading(false); }
  }, [page, filterTeacherId, filterMonth, filterStudentName, filterStatus, filterAnnoCompetenza, sortBy, anno]);

  useEffect(() => { loadPayments(); }, [loadPayments]);
  useEffect(() => { setPage(1); }, [filterTeacherId, filterMonth, filterStudentName, filterStatus, filterAnnoCompetenza]);

  const clearFilters = () => { setFilterTeacherId(''); setFilterMonth(''); setFilterStudentName(''); setFilterStatus(''); setFilterAnnoCompetenza(''); setPage(1); };
  const hasFilters = filterTeacherId || (filterMonth && filterMonth !== 'all') || filterStudentName || (filterStatus && filterStatus !== 'all') || (filterAnnoCompetenza && filterAnnoCompetenza !== 'all');

  return (
    <>
      <div className="mb-8 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Gestione Pagamenti</h1>
        <p className="text-[#52525B] text-sm">Visualizza e gestisci tutti gli addebiti della scuola</p>
        {dataEsportazione && (
          <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2D2A4A] text-white rounded-sm mt-1" data-testid="admin-data-esportazione">
            <span className="text-sm font-semibold tracking-wide">Dati aggiornati al {formatDate(dataEsportazione)}</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 animate-fade-in animate-delay-1">
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-4 text-center" data-testid="admin-payments-total">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1">Totale Addebiti</div>
          <div className="text-2xl font-bold font-['Work_Sans']">{formatCurrency(totals.total_amount)}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-center" data-testid="admin-payments-paid">
          <div className="text-xs font-semibold uppercase tracking-wider text-green-700 mb-1">Importo Pagato</div>
          <div className="text-2xl font-bold font-['Work_Sans'] text-green-800">{formatCurrency(totals.paid_amount)}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 text-center" data-testid="admin-payments-dovuto">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1">Dovuto Insegnante</div>
          <div className="text-2xl font-bold font-['Work_Sans'] text-blue-800">{formatCurrency(totals.dovuto_insegnante)}</div>
        </div>
      </div>
      <div className="flex items-center gap-3 mb-4 flex-wrap animate-fade-in animate-delay-2">
        <Select value={filterTeacherId} onValueChange={setFilterTeacherId}>
          <SelectTrigger className={`w-full sm:w-[250px] rounded-sm h-10 text-sm font-semibold border-2 ${filterTeacherId ? 'border-[#2D2A4A] bg-[#2D2A4A] text-white' : 'border-[#2D2A4A] text-[#2D2A4A]'}`} data-testid="filter-admin-teacher"><SelectValue placeholder="Tutti gli insegnanti" /></SelectTrigger>
          <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name?.toUpperCase()}</SelectItem>)}</SelectContent>
        </Select>
        <Funnel size={16} className="text-[#52525B] hidden sm:block" />
        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger className="w-full sm:w-[200px] rounded-sm h-9 text-sm" data-testid="filter-admin-month"><SelectValue placeholder="Tutti i periodi" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti i periodi</SelectItem>{availableMonths.map(m => <SelectItem key={m} value={m}>{formatMonth(m)}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="text" placeholder="Cerca alunno..." value={filterStudentName} onChange={(e) => setFilterStudentName(e.target.value)} className="w-full sm:w-[200px] rounded-sm h-9 text-sm uppercase" data-testid="filter-admin-student" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-[150px] rounded-sm h-9 text-sm" data-testid="filter-admin-status"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem><SelectItem value="Pagato">Pagato</SelectItem><SelectItem value="Non Pagato">Non Pagato</SelectItem></SelectContent>
        </Select>
        <Select value={filterAnnoCompetenza} onValueChange={setFilterAnnoCompetenza}>
          <SelectTrigger className="w-full sm:w-[160px] rounded-sm h-9 text-sm" data-testid="filter-admin-anno-competenza"><SelectValue placeholder="Anno Competenza" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli anni</SelectItem>{availableAnniCompetenza.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sortBy || 'default'} onValueChange={(v) => setSortBy(v === 'default' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-[240px] rounded-sm h-9 text-sm" data-testid="filter-admin-payments-sort"><SelectValue placeholder="Ordina per..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Scadenza (dalla più vecchia)</SelectItem>
            <SelectItem value="teacher">Insegnante · Scadenza · Alunno</SelectItem>
            <SelectItem value="student">Alunno · Scadenza</SelectItem>
            <SelectItem value="scadenza">Scadenza · Alunno</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-sm text-[#52525B]" data-testid="clear-admin-payment-filters"><X size={14} className="mr-1" />Pulisci</Button>}
        <Button variant="outline" size="sm" onClick={async () => {
          try {
            const params = {};
            if (filterTeacherId) params.teacher_id = filterTeacherId;
            if (filterMonth && filterMonth !== 'all') params.month = filterMonth;
            if (filterStudentName) params.student_name = filterStudentName;
            if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
            if (filterAnnoCompetenza && filterAnnoCompetenza !== 'all') params.anno_competenza = filterAnnoCompetenza;
            const res = await api.downloadPaymentsEstrattoContoPdf(params);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Estratto_Conto.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } catch { toast.error('Nessun dato da stampare con i filtri selezionati'); }
        }} className="h-9 text-sm rounded-sm" data-testid="print-estratto-conto"><Printer size={16} className="mr-1" />Stampa Estratto Conto</Button>
        <div className="text-sm text-[#52525B] font-medium ml-auto" data-testid="admin-payments-count">{total} addebiti</div>
      </div>
      <div className="animate-fade-in animate-delay-3 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        {loading ? (
          <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
        ) : payments.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm">
            <CurrencyEur size={48} className="mx-auto text-[#E4E4E7] mb-4" />
            <p className="text-[#52525B]">{hasFilters ? 'Nessun addebito trovato' : 'Nessun addebito importato. Usa "Importa Addebiti" per iniziare.'}</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
            <table className="w-full" data-testid="admin-payments-table">
              <thead><tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Insegnante</th>
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Anno Comp.</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Periodo</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Alunno</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Descrizione</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Importo</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Scadenza</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Pagamento</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Stato</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Dovuto Insegnante</th>
              </tr></thead>
              <tbody>{payments.map(p => (
                <tr key={p.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`admin-payment-row-${p.id}`}>
                  <td className="px-3 py-1.5 text-sm">{p.teacher_name}</td>
                  <td className="px-3 py-1.5 text-sm text-center font-['Work_Sans']">{p.anno_competenza || '-'}</td>
                  <td className="px-3 py-1.5 text-sm">
                    <input
                      type="month"
                      defaultValue={p.month || ''}
                      onBlur={async (e) => {
                        const newVal = e.target.value;
                        if (newVal === (p.month || '')) return;
                        try {
                          await api.updateAdminPayment(p.id, { month: newVal });
                          setPayments(prev => prev.map(x => x.id === p.id ? { ...x, month: newVal } : x));
                          toast.success('Periodo aggiornato');
                        } catch (err) { toast.error(err.response?.data?.detail || 'Errore aggiornamento'); e.target.value = p.month || ''; }
                      }}
                      className="border border-transparent hover:border-[#E4E4E7] focus:border-[#2D2A4A] rounded px-2 py-0.5 text-sm outline-none bg-transparent"
                      data-testid={`edit-month-${p.id}`}
                    />
                  </td>
                  <td className="px-3 py-1.5 font-medium uppercase text-sm">{p.student_name}</td>
                  <td className="px-3 py-1.5 text-sm text-[#52525B] max-w-[180px] truncate">{p.descrizione || '-'}</td>
                  <td className="px-3 py-1.5 text-sm text-right font-medium font-['Work_Sans']">{formatCurrency(p.importo)}</td>
                  <td className="px-3 py-1.5 text-sm text-[#52525B]">
                    <input
                      type="date"
                      defaultValue={p.data_scadenza || ''}
                      onBlur={async (e) => {
                        const newVal = e.target.value;
                        if (newVal === (p.data_scadenza || '')) return;
                        try {
                          await api.updateAdminPayment(p.id, { data_scadenza: newVal });
                          setPayments(prev => prev.map(x => x.id === p.id ? { ...x, data_scadenza: newVal } : x));
                          toast.success('Scadenza aggiornata');
                        } catch (err) { toast.error(err.response?.data?.detail || 'Errore aggiornamento'); e.target.value = p.data_scadenza || ''; }
                      }}
                      className="border border-transparent hover:border-[#E4E4E7] focus:border-[#2D2A4A] rounded px-2 py-0.5 text-sm outline-none bg-transparent"
                      data-testid={`edit-scadenza-${p.id}`}
                    />
                  </td>
                  <td className="px-3 py-1.5 text-sm text-[#52525B]">{formatDate(p.data_pagamento)}</td>
                  <td className="px-3 py-1.5"><span className={`px-2 py-0.5 rounded text-xs font-medium ${p.status === 'Pagato' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{p.status}</span></td>
                  <td className="px-3 py-1.5 text-sm text-right font-medium font-['Work_Sans']">{p.status === 'Pagato' ? formatCurrency(p.importo_insegnante) : ''}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <div className="text-sm text-[#52525B]">Pagina {page} di {pages}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-sm" data-testid="prev-admin-payment-page"><CaretLeft size={14} className="mr-1" />Precedente</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} className="rounded-sm" data-testid="next-admin-payment-page">Successiva<CaretRight size={14} className="ml-1" /></Button>
          </div>
        </div>
      )}
    </>
  );
}
